/**
 * API Client
 * Wrapper for fetch with credentials and error handling
 */

const API_BASE = '/api/v1';

export interface ApiError {
    success: false;
    error: string;
}

export interface ApiSuccess<T> {
    success: true;
    data: T;
}

export type ApiResponse<T> = ApiSuccess<T> | ApiError;

async function request<T>(
    endpoint: string,
    options: RequestInit = {}
): Promise<ApiResponse<T>> {
    try {
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            ...(options.headers as Record<string, string>),
        };

        if (options.body instanceof FormData) {
            delete headers['Content-Type'];
        }

        const response = await fetch(`${API_BASE}${endpoint}`, {
            ...options,
            credentials: 'include',
            headers,
        });

        const data = await response.json();
        return data as ApiResponse<T>;
    } catch (error) {
        return {
            success: false,
            error: 'Network error. Please try again.',
        };
    }
}

export const api = {
    // Auth
    signup: (email: string, password: string) =>
        request<{ user: any; settings: any }>('/auth/signup', {
            method: 'POST',
            body: JSON.stringify({ email, password }),
        }),

    login: (email: string, password: string) =>
        request<{ user: any; settings: any }>('/auth/login', {
            method: 'POST',
            body: JSON.stringify({ email, password }),
        }),

    logout: () =>
        request('/auth/logout', { method: 'POST' }),

    getMe: () =>
        request<{ user: any; settings: any }>('/me'),

    // Market Data
    getStockPrice: (symbol: string, market: string) =>
        request<{
            symbol: string;
            market: string;
            currentPrice: number;
            previousClose: number;
            change: number;
            changePercent: number;
            updatedAt: string;
            stockName?: string;
        }>(`/market-data/price?symbol=${symbol}&market=${market}`),

    updateSettings: (defaultStopLossPct: number) =>
        request('/me/settings', {
            method: 'PATCH',
            body: JSON.stringify({ defaultStopLossPct }),
        }),

    // Positions
    createPosition: (data: any) =>
        request('/positions', {
            method: 'POST',
            body: JSON.stringify(data),
        }),

    getPositions: (status?: string) =>
        request<any[]>(`/positions${status ? `?status=${status}` : ''}`),

    getPosition: (id: string) =>
        request<any>(`/positions/${id}`),

    updatePosition: (id: string, data: any) =>
        request(`/positions/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(data),
        }),

    closePosition: (id: string, closePrice?: number) =>
        request(`/positions/${id}/close`, {
            method: 'POST',
            body: closePrice !== undefined ? JSON.stringify({ closePrice }) : undefined,
        }),

    deletePosition: (id: string) =>
        request(`/positions/${id}`, { method: 'DELETE' }),

    // Fills
    addFill: (positionId: string, data: any) =>
        request(`/positions/${positionId}/fills`, {
            method: 'POST',
            body: JSON.stringify(data),
        }),

    updateFill: (id: string, data: any) =>
        request(`/fills/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(data),
        }),

    deleteFill: (id: string) =>
        request(`/fills/${id}`, { method: 'DELETE' }),

    // Uploads
    uploadFile: (file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        return request<{ fileKey: string; fileUrl: string }>('/upload', {
            method: 'POST',
            body: formData,
            // fetch automatically sets Content-Type to multipart/form-data with boundary
            // so we don't set it manually here, effectively overriding the default json header
            headers: {},
        });
    },

    // Attachments
    addAttachment: (positionId: string, data: any) =>
        request(`/positions/${positionId}/attachments`, {
            method: 'POST',
            body: JSON.stringify(data),
        }),

    // Review
    getReviews: () => request<any[]>('/reviews'),

    // Market Data
    getMarketHistory: () => request<any[]>('/market-data/history'),
    updateMarketData: () => request('/market-data/update', { method: 'POST' }),

    deleteAttachment: (id: string) =>
        request(`/attachments/${id}`, { method: 'DELETE' }),

    // News Clips
    getLinkPreview: (url: string) =>
        request<{ url: string; title: string | null; image: string | null; description: string | null }>('/link-preview', {
            method: 'POST',
            body: JSON.stringify({ url }),
        }),

    addNewsClip: (positionId: string, data: any) =>
        request(`/positions/${positionId}/news-clips`, {
            method: 'POST',
            body: JSON.stringify(data),
        }),

    updateNewsClip: (id: string, data: any) =>
        request(`/news-clips/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(data),
        }),

    deleteNewsClip: (id: string) =>
        request(`/news-clips/${id}`, { method: 'DELETE' }),

    // Reviews
    createReview: (positionId: string, data: any) =>
        request(`/positions/${positionId}/review`, {
            method: 'POST',
            body: JSON.stringify(data),
        }),

    // Review Entries
    addReviewEntry: (positionId: string, data: any) =>
        request(`/positions/${positionId}/review-entries`, {
            method: 'POST',
            body: JSON.stringify(data),
        }),

    getReviewEntries: (positionId: string) =>
        request<any[]>(`/positions/${positionId}/review-entries`),

    pinReviewEntry: (id: string) =>
        request(`/review-entries/${id}/pin`, { method: 'POST' }),

    deleteReviewEntry: (id: string) =>
        request(`/review-entries/${id}`, { method: 'DELETE' }),

    // Daily Plans
    getActivePlan: (date?: string) =>
        request<any>(`/daily-plans/active${date ? `?date=${date}` : ''}`),

    getPlanVersions: (planDate: string) =>
        request<any[]>(`/daily-plans/versions?planDate=${planDate}`),

    createPlanVersion: (data: any) =>
        request('/daily-plans/versions', {
            method: 'POST',
            body: JSON.stringify(data),
        }),

    pinPlanVersion: (id: string) =>
        request(`/daily-plans/versions/${id}/pin`, { method: 'POST' }),

    // Home
    getHome: (date?: string) =>
        request<any>(`/home?t=${Date.now()}${date ? `&date=${date}` : ''}`),

    // Insights
    getInsights: (from: string, to: string) =>
        request<any>(`/insights/summary?from=${from}&to=${to}`),

    // Market Data
    // updateMarketData moved to top section

    // Journal Images
    getJournalImages: (date: string) =>
        request<any[]>(`/journal-images?date=${date}`),

    addJournalImage: (data: { journalDate: string; r2Key: string; url: string; caption?: string }) =>
        request('/journal-images', {
            method: 'POST',
            body: JSON.stringify(data),
        }),

    deleteJournalImage: (id: string) =>
        request(`/journal-images/${id}`, { method: 'DELETE' }),

    // General Request
    request: request,

    // Market Briefing
    getMarketBriefing: (date?: string) =>
        request(`/market-data/briefing/fetch?date=${date || ''}`),

    // AI Analysis
    analyzeMarket: (date: string) =>
        request<any>('/market-data/analyze', {
            method: 'POST',
            body: JSON.stringify({ date }),
        }),
};


